<template>
  <div class="location-section">
    <location-section-suggestable-input
      :input-value="inputValue"
      :suggestions="mergedSuggestions"
      :focused="focused"
      :force-suggestions-open="forceSuggestionsOpen"
      :placeholder="inputPlaceholder"
      :override-hide-clearable="overrideHideClearable"
      :search-type="searchType"
      :show-help-text="!showInputOnly"
      @campsite-selected="suggestionSelected"
      @custom-location-selected="suggestionSelected"
      @hierarchy-selected="suggestionSelected"
      @recent-search-selected="recentSearchSelected"
      @input-changed="updateSuggestions"
      @clear-input="$emit('clearInput')"
      @autosuggestion-closed="$emit('autosuggestion-closed')"
      @focus="$emit('focus')"
      @blur="$emit('blur')"
    />
    <div
      v-show="!showInputOnly"
      data-test="search-wizard-location-options"
      :class="[
        { 'point-search': searchWizardStore.shouldShowWithinInput },
        'location-options',
      ]"
    >
      <lazy-within-input
        v-if="!$isDesktop && searchWizardStore.shouldShowWithinInput"
        :value="within"
        @input="$emit('modifyWithin', $event)"
      />
      <geolocation-checkbox
        v-if="!$isDesktop"
        :current-point="searchWizardStore.myLocationPoint"
        @point-change="pointChanged($event)"
      />
    </div>
  </div>
</template>

<script lang="ts">
import type { GeoPoint } from '~/utility/geo/GeoLocation'
import { getDefaultWithinForLocale } from '~/utility/geo/distance-units'
import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'
import { logger } from '../../../../../utility/logger'
import {
  LocationWizardCampsiteData,
  LocationWizardCustomData,
  LocationWizardData,
  LocationWizardHierarchyData,
} from '../../../domain/LocationWizardData'
import { LocationSuggestions } from '../domain/LocationSuggestions'
import { AutoSuggestData } from '../types'

interface SearchTerm {
  term?: string
}
type LocationWizardDataEvent =
  | (LocationWizardCampsiteData & SearchTerm)
  | (LocationWizardCustomData & SearchTerm)
  | (LocationWizardHierarchyData & SearchTerm)

@Component({
  emits: [
    'updateSuggestions',
    'modifyWithin',
    'locationSelected',
    'input-changed',
    'clearInput',
    'focus',
    'useLocationChange',
    'autosuggestion-closed',
  ],
  setup() {
    const searchWizardStore = useSearchWizardStore(usePinia())
    return { searchWizardStore }
  },
})
class LocationSection extends Vue {
  searchWizardStore: ReturnType<typeof useSearchWizardStore>

  get area() {
    return this.searchWizardStore.context.filters.area
  }

  @Prop({ default: '' })
  inputValue: string

  @Prop({ required: true })
  focused: boolean

  @Prop()
  selectedLocationType: string | null

  @Prop()
  selectedLocation: LocationWizardData | null

  @Prop()
  inputPlaceholder?: string

  @Prop()
  hasBounds: boolean

  @Prop()
  searchInput: string

  @Prop()
  suggestions: LocationSuggestions

  @Prop()
  forceSuggestionsOpen: boolean

  @Prop({ default: false })
  showInputOnly?: boolean

  @Prop({ default: false })
  overrideHideClearable: boolean

  updateSuggestions(input: string) {
    this.$emit('input-changed', input)
    this.$emit('updateSuggestions', input)
  }

  suggestionSelected(data: LocationWizardDataEvent) {
    const { term, ...location } = data
    this.$emit('locationSelected', location, term)
  }

  async recentSearchSelected(url) {
    await this.$router.push(url)
  }

  get within(): number {
    /* eslint-disable @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access -- Nuxt $config.public type needs refinement */
    return (
      this.searchWizardStore.within || this.$config.public.defaultWithinRange
    )
    /* eslint-enable @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access */
  }

  get mergedSuggestions(): AutoSuggestData {
    const suggestionsFormatted: AutoSuggestData = []
    if (this.suggestions.recentSearches) {
      suggestionsFormatted.push({
        name: 'recentSearch',
        data: this.suggestions.recentSearches,
      })
    }
    if (this.suggestions.hierarchies) {
      suggestionsFormatted.push({
        name: 'hierarchy',
        data: this.suggestions.hierarchies,
      })
    }
    if (this.suggestions.campsites) {
      suggestionsFormatted.push({
        name: 'campsite',
        data: this.suggestions.campsites,
      })
    }
    if (this.suggestions.custom) {
      suggestionsFormatted.push({
        name: 'custom',
        data: this.suggestions.custom,
      })
    }
    return suggestionsFormatted
  }

  get searchType() {
    return this.searchWizardStore?.context?.searchInputValue?.type || ''
  }

  async pointChanged(point: GeoPoint | null) {
    logger('pointChanged', { point })
    // if we have a point, send it to the wizard with default within

    // otherwise clear the existing point
    const area = point
      ? {
          ...point,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Nuxt $config.public type needs refinement
          within: this.$config.public.defaultWithinRange,
        }
      : undefined
    if (area) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Nuxt $config.public type needs refinement
      const defaultMiles = this.$config.public.defaultNearMeWithinRange
      const defaultInLocaleUnit = getDefaultWithinForLocale(
        defaultMiles,
        this.$i18n.locale,
      )
      this.$emit('clearInput', true)
      this.$emit('input-changed', '')
      this.searchWizardStore.send('SET_MY_LOCATION', {
        area,
        within: defaultInLocaleUnit,
      })
    } else if (
      this.searchWizardStore.context.filters.area &&
      !this.searchWizardStore.context.filters.area?.isEmptyPoint &&
      !this.searchWizardStore.context.filters.placename
    ) {
      this.searchWizardStore.send('CLEAR_INPUT')
    }

    await this.searchWizardStore.updateCounts()
  }

  @Watch('open')
  onOpenChange(opened: boolean) {
    if (opened && this.searchInput.length > 0) {
      this.updateSuggestions('')
    }
  }
}

export default toNative(LocationSection)
</script>

<style scoped>
label {
  color: var(--colour-wcag-blue);
  display: block;
  margin: -0.5em 0 0.5em 0;
}

.location-options {
  align-items: center;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  margin: 0.5em 0 1em;
}

.location-options.point-search :deep(.error) {
  padding: 0.5em 0 0 0;
}

.location-section {
  -webkit-tap-highlight-color: transparent;
}
</style>
